// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useState } from 'react'
import { Mail, Plus, Trash2 } from 'lucide-react'
import * as api from '../../api/client'

/**
 * Portal-entered ALTERNATE contact emails for a patient or prescriber (mig 420). PioneerRx remains
 * the source of truth for the primary email; these are additive fallbacks the pharmacy types in when
 * PioneerRx has none, and are offered as options when sending a document for e-signature. Adding one
 * here never changes the PioneerRx-owned email.
 */
export default function OtherEmailsSection({ subjectType, subjectId }: {
  subjectType: 'patient' | 'prescriber'; subjectId: string
}) {
  const [data, setData] = useState<api.ContactEmailsResponse | null>(null)
  const [email, setEmail] = useState('')
  const [label, setLabel] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const load = () => { api.listContactEmails(subjectType, subjectId).then(setData).catch(() => setData(null)) }
  useEffect(() => { if (subjectId) load() }, [subjectId, subjectType])   // eslint-disable-line react-hooks/exhaustive-deps

  const valid = /.+@.+\..+/.test(email.trim())
  const add = async () => {
    if (!valid) return
    setBusy(true); setErr(null)
    try {
      await api.addContactEmail(subjectType, subjectId, email.trim(), label.trim() || undefined)
      setEmail(''); setLabel(''); load()
    } catch (e: any) { setErr(e?.body?.message ?? e?.body?.error ?? 'Could not add email') }
    finally { setBusy(false) }
  }
  const del = async (id: string) => {
    setBusy(true); setErr(null)
    try { await api.deleteContactEmail(subjectType, subjectId, id); load() }
    catch (e: any) { setErr(e?.body?.message ?? 'Could not remove email') }
    finally { setBusy(false) }
  }

  const primary = data?.primary_email
  const alternates = data?.alternates ?? []

  return (
    <section style={wrap}>
      <div style={hdr}><Mail size={15} aria-hidden="true" /> <span>Other emails</span></div>
      <div style={sub}>
        PioneerRx primary: <b style={{ color: 'var(--color-text)' }}>{primary || 'none on file'}</b>. Add
        alternates below — offered as options when sending for signature. These don&rsquo;t change PioneerRx.
      </div>
      {alternates.length === 0
        ? <div style={empty}>No other emails yet.</div>
        : <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 10px' }}>
            {alternates.map(a => (
              <li key={a.id} style={row}>
                <span style={{ fontSize: 13, color: 'var(--color-text)' }}>
                  {a.email}{a.label ? <span style={tag}>{a.label}</span> : null}
                </span>
                <button onClick={() => del(a.id)} disabled={busy} title="Remove" aria-label={`Remove ${a.email}`} style={delBtn}>
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@example.com"
          style={{ ...inp, flex: 2, minWidth: 160 }} />
        <input value={label} onChange={e => setLabel(e.target.value)} placeholder="label (optional)"
          style={{ ...inp, flex: 1, minWidth: 100 }} />
        <button onClick={add} disabled={!valid || busy}
          style={{ ...addBtn, opacity: valid && !busy ? 1 : .5, cursor: valid && !busy ? 'pointer' : 'not-allowed' }}>
          <Plus size={14} /> Add
        </button>
      </div>
      {err && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 6 }}>{err}</div>}
    </section>
  )
}

const wrap: React.CSSProperties = { border: '1px solid var(--color-border,#e2e8f0)', borderRadius: 10, padding: 14, marginTop: 14, background: 'var(--color-surface,#fff)' }
const hdr: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 7, fontSize: 13.5, fontWeight: 700, color: 'var(--color-text,#0f172a)', marginBottom: 6 }
const sub: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-soft,#64748b)', marginBottom: 10, lineHeight: 1.5 }
const empty: React.CSSProperties = { fontSize: 12.5, color: 'var(--color-text-mute,#94a3b8)', marginBottom: 10 }
const row: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--color-border,#eef2f6)' }
const tag: React.CSSProperties = { marginLeft: 8, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', padding: '1px 6px', borderRadius: 6, background: 'var(--color-surface-soft,#f1f5f9)', color: 'var(--color-text-soft,#64748b)' }
const delBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', border: 'none', background: 'transparent', color: '#dc2626', cursor: 'pointer', padding: 4 }
const inp: React.CSSProperties = { boxSizing: 'border-box', padding: '7px 9px', borderRadius: 8, border: '1px solid var(--color-border,#cbd5e1)', fontSize: 13, background: 'var(--color-surface,#fff)', color: 'var(--color-text,#0f172a)' }
const addBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 8, border: 'none', background: 'var(--color-primary,#0891b2)', color: '#fff', fontWeight: 600, fontSize: 13 }
